import { computed, inject } from '@angular/core';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, EMPTY, exhaustMap, map, mergeMap, Observable, pipe, switchMap, tap } from 'rxjs';
import { ApiErrorResponse } from '@bn/api-interfaces';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState } from '@bn/shared/state-store';
import { BnToastrService } from '@bn/web-ui/toastr';
import { EventLogAppliedFilter } from '../models';
import { EventGroup, EventLogsExportRequest, EventLogsFilter, EventLogsSearchRequest, EventLogsSearchResponse, EventLogsService, EventRow } from '../openapi';

const HOUR_MS = 60 * 60 * 1000;
const DEFAULT_WINDOW_MS = 24 * HOUR_MS;
const MAX_WINDOW_MS = 72 * HOUR_MS;
const DEFAULT_PAGE_SIZE = 50;
export const NESTED_PAGE_SIZE = 50;
const SPARKLINE_BUCKETS = 20;

const events = getStoreEvents('SHIELD_EVENT_LOGS_SEARCH', ['EXPORT', 'GROUP_ROWS']);

const INITIAL_REQUEST: EventLogsSearchRequest = {
  from: undefined,
  to: undefined,
  query: '',
  filters: [],
  groupBy: [],
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

export interface EventLogGroupRows {
  rows: EventRow[];
  page: number;
  total: number;
}

export interface EventLogGroupRowsRequest {
  group: EventGroup;
  page: number;
}

export interface EventLogsSearchModel {
  shieldZoneId: number;
  request: EventLogsSearchRequest;
  rows: EventRow[];
  groups: EventGroup[];
  total: number;
  totalPages: number;
  page: number;
  expandedGroupRows: Record<string, EventLogGroupRows>;
  loadingGroupKeys: string[];
  requestEpoch: number;
  hasSearched: boolean;
}

const initialState: EventLogsSearchModel = {
  shieldZoneId: 0,
  request: INITIAL_REQUEST,
  rows: [],
  groups: [],
  total: 0,
  totalPages: 0,
  page: 0,
  expandedGroupRows: {},
  loadingGroupKeys: [],
  requestEpoch: 0,
  hasSearched: false,
};

export function groupKeyString(key?: { [key: string]: string } | null): string {
  return JSON.stringify(key ?? {});
}

function clampWindow(request: EventLogsSearchRequest): EventLogsSearchRequest {
  const now = Date.now();
  let to = Math.min(request.to ?? now, now);
  let from = Math.max(request.from ?? to - DEFAULT_WINDOW_MS, now - MAX_WINDOW_MS);

  if (from >= to) {
    to = now;
    from = now - DEFAULT_WINDOW_MS;
  }

  return { ...request, from, to };
}

function safeParseLog(log: unknown): unknown {
  if (typeof log !== 'string') return log;

  try {
    return JSON.parse(log);
  } catch {
    return log;
  }
}

function normalizeRow(row: EventRow): EventRow {
  return { ...row, log: safeParseLog(row.log) };
}

function downloadBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl));
}

export const AppShieldEventLogsSearchStore = signalStore(
  withDevtools('Shield.EventLogsSearch'),
  withDefaultRequestStatusState(),
  withState<EventLogsSearchModel>(initialState),
  withComputed((store) => ({
    isGrouped: computed(() => (store.request().groupBy?.length ?? 0) > 0),
    groupByDims: computed(() => store.request().groupBy ?? []),
    appliedFilters: computed(() => (store.request().filters ?? []) as EventLogAppliedFilter[]),
    query: computed(() => store.request().query ?? ''),
    from: computed(() => store.request().from),
    to: computed(() => store.request().to),
    pageSize: computed(() => store.request().pageSize ?? DEFAULT_PAGE_SIZE),
  })),
  withMethods((store, service = inject(EventLogsService), toastr = inject(BnToastrService)) => {
    const performSearch = (request: EventLogsSearchRequest): Observable<EventLogsSearchResponse> => {
      updateState(store, events.LIST.REQUEST, { hasSearched: true }, store.setLoading(StoreRequest.List));

      const body: EventLogsSearchRequest = (request.groupBy?.length ?? 0) > 0 ? { ...request, buckets: SPARKLINE_BUCKETS } : request;

      return service.searchEventLogs({ shieldZoneId: store.shieldZoneId(), eventLogsSearchRequest: body }).pipe(
        tapResponse({
          next: (response: EventLogsSearchResponse) => {
            if (response.errorResponse) {
              updateState(
                store,
                events.LIST.ERROR,
                { rows: [], groups: [], total: 0, totalPages: 0, expandedGroupRows: {} },
                store.setErrorMessage(StoreRequest.List, response.errorResponse.message ?? 'Failed to load event logs.'),
              );

              return;
            }

            updateState(
              store,
              events.LIST.SUCCESS,
              {
                rows: (response.rows ?? []).map(normalizeRow),
                groups: response.groups ?? [],
                total: response.total ?? 0,
                totalPages: response.totalPages ?? 0,
                page: response.page ?? request.page ?? 0,
                expandedGroupRows: {},
              },
              store.setFinished(StoreRequest.List),
            );
          },
          error: (error: ApiErrorResponse) =>
            updateState(
              store,
              events.LIST.ERROR,
              { rows: [], groups: [], total: 0, totalPages: 0, expandedGroupRows: {} },
              store.setError(StoreRequest.List, error),
            ),
        }),
      );
    };

    const mergeAndClamp = (partial: Partial<EventLogsSearchRequest>): EventLogsSearchRequest => {
      const request = clampWindow({ ...store.request(), ...partial });
      patchState(store, { request, requestEpoch: store.requestEpoch() + 1 });

      return request;
    };

    const runSearch = (request: EventLogsSearchRequest): Observable<EventLogsSearchResponse> => (store.shieldZoneId() ? performSearch(request) : EMPTY);

    const search = rxMethod<Partial<EventLogsSearchRequest>>(pipe(map(mergeAndClamp), switchMap(runSearch)));

    const searchDebounced = rxMethod<{ partial: Partial<EventLogsSearchRequest>; shieldZoneId: number }>(
      pipe(
        debounceTime(500),
        tap(({ partial, shieldZoneId }) => {
          if (shieldZoneId === store.shieldZoneId()) search(partial);
        }),
      ),
    );

    const removeLoadingGroupKey = (key: string): string[] => store.loadingGroupKeys().filter((loadingKey) => loadingKey !== key);

    const loadGroupRows = rxMethod<EventLogGroupRowsRequest>(
      mergeMap(({ group, page }) => {
        const key = groupKeyString(group.key);
        const loaded = store.expandedGroupRows()[key];

        if (loaded?.page === page || store.loadingGroupKeys().includes(key)) return EMPTY;

        const epoch = store.requestEpoch();
        updateState(store, events.GROUP_ROWS.REQUEST, { loadingGroupKeys: [...store.loadingGroupKeys(), key] });

        const keyFilters: EventLogsFilter[] = Object.entries(group.key ?? {}).map(([field, value]) => ({ field, op: 'eq', value: [value] }));
        const base = store.request();
        const request: EventLogsSearchRequest = {
          from: base.from,
          to: base.to,
          query: base.query,
          filters: [...(base.filters ?? []), ...keyFilters],
          page,
          pageSize: NESTED_PAGE_SIZE,
        };

        return service.searchEventLogs({ shieldZoneId: store.shieldZoneId(), eventLogsSearchRequest: request }).pipe(
          tapResponse({
            next: (response: EventLogsSearchResponse) => {
              if (epoch !== store.requestEpoch()) {
                updateState(store, events.GROUP_ROWS.CLEAR, { loadingGroupKeys: removeLoadingGroupKey(key) });

                return;
              }

              if (response.errorResponse) {
                const message = response.errorResponse.message ?? 'Failed to load group events.';
                updateState(
                  store,
                  events.GROUP_ROWS.ERROR,
                  { loadingGroupKeys: removeLoadingGroupKey(key) },
                  store.setErrorMessage(StoreRequest.Item, message),
                );
                toastr.danger(message);

                return;
              }

              updateState(store, events.GROUP_ROWS.SUCCESS, {
                expandedGroupRows: {
                  ...store.expandedGroupRows(),
                  [key]: { rows: (response.rows ?? []).map(normalizeRow), page: response.page ?? page, total: response.total ?? 0 },
                },
                loadingGroupKeys: removeLoadingGroupKey(key),
              });
            },
            error: (error: ApiErrorResponse) => {
              updateState(store, events.GROUP_ROWS.ERROR, { loadingGroupKeys: removeLoadingGroupKey(key) }, store.setError(StoreRequest.Item, error));
              toastr.danger('Failed to load group events.');
            },
          }),
        );
      }),
    );

    const exportCsv = rxMethod<void>(
      exhaustMap(() => {
        const shieldZoneId = store.shieldZoneId();

        if (!shieldZoneId) return EMPTY;

        updateState(store, events.EXPORT.REQUEST, store.setLoading(StoreRequest.Update));

        const base = store.request();
        const body: EventLogsExportRequest = { from: base.from, to: base.to, query: base.query, filters: base.filters };

        return service.exportEventLogs({ shieldZoneId, eventLogsExportRequest: body }, 'body', false, { httpHeaderAccept: 'text/csv' }).pipe(
          tapResponse({
            next: (csv) => {
              const blob = csv instanceof Blob ? csv : new Blob([String(csv)], { type: 'text/csv' });
              downloadBlob(blob, `shield-event-logs-${shieldZoneId}.csv`);
              updateState(store, events.EXPORT.SUCCESS, store.setFinished(StoreRequest.Update));
            },
            error: (error: ApiErrorResponse) => updateState(store, events.EXPORT.ERROR, store.setError(StoreRequest.Update, error)),
          }),
        );
      }),
    );

    return {
      search,
      searchDebounced,
      loadGroupRows,
      exportCsv,

      setShieldZoneId(shieldZoneId: number): void {
        if (!shieldZoneId || shieldZoneId === store.shieldZoneId()) return;

        const now = Date.now();
        patchState(store, {
          shieldZoneId,
          request: { ...INITIAL_REQUEST, from: now - DEFAULT_WINDOW_MS, to: now },
          rows: [],
          groups: [],
          expandedGroupRows: {},
          loadingGroupKeys: [],
          total: 0,
          totalPages: 0,
          page: 0,
        });
        search({ page: 0 });
      },

      setQuery(query: string): void {
        patchState(store, { request: { ...store.request(), query, page: 0 } });
        searchDebounced({ partial: {}, shieldZoneId: store.shieldZoneId() });
      },

      setDateRange(from: number, to: number): void {
        search({ from, to, page: 0 });
      },

      setGroupBy(groupBy: string[]): void {
        search({ groupBy, page: 0 });
      },

      setPage(page: number): void {
        search({ page });
      },

      setFilters(filters: EventLogsFilter[]): void {
        search({ filters, page: 0 });
      },

      refresh(): void {
        const { from, to } = store.request();
        const now = Date.now();

        patchState(store, { expandedGroupRows: {}, loadingGroupKeys: [] });

        if (!to || new Date(to).toDateString() === new Date(now).toDateString()) {
          const duration = from && to ? to - from : DEFAULT_WINDOW_MS;
          search({ from: now - duration, to: now, page: 0 });
        } else {
          search({ page: 0 });
        }
      },
    };
  }),
);
